import React, { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import Palette from "assets/styles/theme/overrides/Palette";
import Typography from "assets/styles/theme/overrides/Typography";
import { useLocation } from "react-router-dom";
import { useProgress } from "./ProgressProvider";

const ThemesProvider = ({ children, baseTheme = {} }: any) => {
  const { currentProfile } = useProgress();
  const location = useLocation();
  const profileState = useSelector((state: any) => state.profileState);

  const appPaths = useMemo(
    () => [
      {
        target: "intro-details",
        required: currentProfile.isAllowedToEdit?.yourDetails,
      },
      {
        target: "intro-loan",
        required: currentProfile.isAllowedToEdit?.yourLoan,
      },
      {
        target: "intro-business",
        required: currentProfile.isAllowedToEdit?.yourBusiness,
      },
      {
        target: "final-business",
        required: currentProfile.isAllowedToEdit?.businessDetails,
      },
      {
        target: "sec-property",
        required: currentProfile.isAllowedToEdit?.propertyDetails,
      },
      {
        target: "sec-liabilities",
        required: currentProfile.isAllowedToEdit?.liabilities,
      },
      {
        target: "verif-review",
        required: currentProfile.isAllowedToEdit?.reviewDetails,
      },
      {
        target: "review-approval",
        required: currentProfile.isAllowedToEdit?.reviewDetails,
      },
      {
        target: "verif-directors",
        required: currentProfile.isAllowedToEdit?.directors,
      },
      {
        target: "final-directors",
        required: currentProfile.isAllowedToEdit?.directors,
      },
      {
        target: "verif-identity",
        required: currentProfile.isAllowedToEdit?.identityVerification,
      },
      {
        target: "final-identity",
        required: currentProfile.isAllowedToEdit?.identityVerification,
      },
      {
        target: "final-solicitor",
        required: currentProfile.isAllowedToEdit?.solicitorDetails,
      },
      {
        target: "final-accountant",
        required: currentProfile.isAllowedToEdit?.accountantDetails,
      },
      {
        target: "final-bank",
        required: currentProfile.isAllowedToEdit?.bankDetails,
      },
      {
        target: "final-agreements",
        required: currentProfile.isAllowedToEdit?.agreements,
      },
    ],
    [currentProfile.isAllowedToEdit],
  );

  const isSblApplyRoute = /^\/sbl(\/|$)/.test(location.pathname);
  const isRndApplyRoute = /^\/rnd(\/|$)/.test(location.pathname);
  const basePath = isSblApplyRoute ? "/sbl" : "/rnd";

  const isPathDisabled = useMemo(() => {
    const matchedPath = appPaths.find((path) =>
      location.pathname.startsWith(`${basePath}/${path.target}`),
    );

    if (matchedPath) {
      if (matchedPath.required !== undefined) return !matchedPath.required;
    }

    return false;
  }, [location.pathname, appPaths]);

  const theme = useMemo(
    () =>
      createTheme({
        spacing: 4,
        palette: Palette(),
        typography: Typography,
        components: {
          MuiTypography: { styleOverrides: {} },
          MuiOutlinedInput: {
            styleOverrides: { root: {} },
            ...(isPathDisabled && {
              defaultProps: {
                disabled: !profileState.brokerAppDialog,
              },
            }),
          },
          MuiFormLabel: {
            styleOverrides: { root: { color: "#9F9F9F" } },
          },
          MuiCheckbox: {
            styleOverrides: { root: { color: "#9F9F9F" } },
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: "#ffffff",
                borderRadius: "10px",
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              paper: { backgroundColor: "#1C2022" },
            },
          },
          MuiListItem: { styleOverrides: { root: {} } },
          MuiMenu: {
            styleOverrides: { paper: { backgroundColor: "#ffffff" } },
          },
          MuiListItemText: {
            styleOverrides: { root: { color: "#ffffff" } },
          },
          MuiListItemIcon: {
            styleOverrides: { root: { minWidth: "40px" } },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: "#171717",
                borderRadius: "20px",
                border: "1px solid #1C2022",
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              colorDefault: { backgroundColor: "#1c2022" },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: "#1c2022",
                padding: "20px",
              },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              colorPrimary: {
                color: "#ffe57a",
                backgroundColor: "#eef0f5",
              },
            },
          },
          MuiTextField: {
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiInput: {
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiFilledInput: {
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiSelect: {
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiNativeSelect: {
            ...(isPathDisabled && {
              defaultProps: {
                disabled: true,
              },
            }),
          },
          MuiChip: {
            ...(location.pathname !== "/sbl/final-agreements" &&
              location.pathname !== "/rnd/final-agreements" &&
              isPathDisabled && {
                defaultProps: {
                  disabled: true,
                },
              }),
          },
        },
      }),
    [isPathDisabled, baseTheme, location.pathname, appPaths],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default ThemesProvider;
